import { useEffect, useMemo, useState } from "react";
import { Loader2, Paperclip, Search, X } from "lucide-react";
import { api, apiError, uploadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Field } from "@/pages/Leads";
import { StatusPill } from "@/components/StockStatus";
import { toast } from "sonner";

const ACTIONS = {
  in: { title: "Add Stock (purchase / receipt)", cta: "Add stock" },
  out: { title: "Use Material (issue / consumption)", cta: "Use material" },
  adjust: { title: "Stock Adjustment", cta: "Post adjustment" },
  transfer: { title: "Move Stock to another place", cta: "Move stock" },
};

const OUT_KINDS = [
  ["consumption", "Used in production"],
  ["damage", "Damaged / wasted"],
  ["dispatch", "Sent out / dispatch"],
  ["sale", "Sold"],
];

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.doc,.docx";

function ItemPicker({ items, value, onChange }) {
  const [q, setQ] = useState("");
  const selected = items.find((i) => i.id === value);
  const matches = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items.slice(0, 8);
    return items
      .filter((i) =>
        `${i.name} ${i.sku || ""} ${(i.aliases || []).join(" ")}`.toLowerCase().includes(term))
      .slice(0, 8);
  }, [items, q]);

  if (selected) {
    return (
      <div className="flex items-center justify-between gap-2 rounded border border-border p-2"
        data-testid="picked-stock-item">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">{selected.name}</div>
          <div className="text-[11px] text-muted-foreground">
            {selected.warehouse_name} • {selected.measure?.primary_display
              || `${selected.quantity} ${selected.unit}`}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {selected.status && <StatusPill status={selected.status} />}
          <Button size="icon" variant="ghost" className="h-6 w-6" data-testid="clear-stock-item"
            onClick={() => { onChange(""); setQ(""); }}>
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-9" data-testid="stock-item-search"
          placeholder="Search an existing item by name or alias"
          value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="mt-1 max-h-44 overflow-y-auto rounded border border-border">
        {matches.length === 0 ? (
          <p className="p-3 text-xs text-muted-foreground">
            No existing item matches. Use “+ New Item” only if the item has never been stocked.
          </p>
        ) : (
          matches.map((i) => (
            <button key={i.id} type="button" data-testid={`pick-item-${i.id}`}
              onClick={() => onChange(i.id)}
              className="flex w-full items-center justify-between gap-2 border-b border-border px-2 py-1.5 text-left text-xs last:border-0 hover:bg-secondary">
              <span className="truncate">
                {i.name}
                <span className="ml-1 text-muted-foreground">({i.warehouse_name})</span>
              </span>
              <span className="shrink-0 font-mono-num text-muted-foreground">
                {i.quantity} {i.unit}
              </span>
            </button>
          ))
        )}
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">
        Existing items are always reused — duplicates are never created.
      </p>
    </div>
  );
}

export default function StockActions({ action, items, warehouses, onClose, onDone }) {
  const [suppliers, setSuppliers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    setForm({
      stock_item_id: "", quantity: "", rate: "", supplier_id: "", invoice_no: "",
      invoice_date: new Date().toISOString().slice(0, 10), amount: "", remarks: "",
      kind: "consumption", direction: "in", to_warehouse_id: "",
    });
    setFiles([]);
  }, [action]);

  useEffect(() => {
    if (action !== "in") return;
    api.get("/suppliers").then(({ data }) => setSuppliers(data)).catch(() => setSuppliers([]));
  }, [action]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const item = items.find((i) => i.id === form.stock_item_id);

  const attach = async (fileList) => {
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of Array.from(fileList)) {
        if (f.size > 8 * 1024 * 1024) {
          toast.error(`${f.name} is larger than 8 MB`);
          continue;
        }
        const r = await uploadFile(f, "inventory_bill", form.stock_item_id || "");
        uploaded.push({ file_id: r.id, filename: r.filename, content_type: r.content_type, size: r.size });
      }
      setFiles((x) => [...x, ...uploaded]);
      if (uploaded.length) toast.success(`${uploaded.length} bill file(s) attached`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    const qty = Number(form.quantity);
    if (!form.stock_item_id) return toast.error("Select an existing item");
    if (!qty || qty <= 0) return toast.error("Enter a quantity greater than zero");
    setBusy(true);
    try {
      if (action === "in") {
        await api.post("/inventory/stock-in", {
          stock_item_id: form.stock_item_id,
          quantity: qty,
          rate: Number(form.rate || 0),
          supplier_id: form.supplier_id,
          invoice_no: form.invoice_no,
          invoice_date: form.invoice_date,
          amount: Number(form.amount || 0),
          remarks: form.remarks,
          files,
        });
        toast.success("Stock in posted and bill filed");
      } else if (action === "out") {
        if (!form.remarks.trim()) return toast.error("A reason is required for stock out");
        await api.post(`/stock/${form.stock_item_id}/movement`, {
          kind: form.kind, quantity: qty, reason: form.remarks,
        });
        toast.success("Stock out posted");
      } else if (action === "adjust") {
        if (!form.remarks.trim()) return toast.error("A reason is required for an adjustment");
        await api.post(`/stock/${form.stock_item_id}/movement`, {
          kind: form.direction === "in" ? "adjustment_in" : "adjustment_out",
          quantity: qty,
          reason: form.remarks,
        });
        toast.success("Adjustment posted with full audit trail");
      } else if (action === "transfer") {
        if (!form.to_warehouse_id) return toast.error("Select the destination warehouse");
        await api.post("/stock/transfer", {
          stock_item_id: form.stock_item_id,
          to_warehouse_id: form.to_warehouse_id,
          quantity: qty,
          notes: form.remarks,
        });
        toast.success("Stock transferred");
      }
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const meta = ACTIONS[action] || {};

  return (
    <Dialog open={!!action} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="stock-action-dialog">
        <DialogHeader>
          <DialogTitle>{meta.title}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-3">
          <Field label="Item (existing only)">
            <ItemPicker items={items} value={form.stock_item_id}
              onChange={(v) => set("stock_item_id", v)} />
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={`Quantity ${item ? `(${item.unit})` : ""}`}>
              <Input type="number" data-testid="action-quantity-input" value={form.quantity}
                onChange={(e) => set("quantity", e.target.value)} />
            </Field>

            {action === "out" && (
              <Field label="Movement type">
                <Select value={form.kind} onValueChange={(v) => set("kind", v)}>
                  <SelectTrigger data-testid="action-out-kind-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {OUT_KINDS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            )}

            {action === "adjust" && (
              <Field label="Direction">
                <Select value={form.direction} onValueChange={(v) => set("direction", v)}>
                  <SelectTrigger data-testid="action-adjust-direction"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in">Increase stock (found / excess)</SelectItem>
                    <SelectItem value="out">Decrease stock (shortage / loss)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            )}

            {action === "transfer" && (
              <Field label="Transfer to warehouse">
                <Select value={form.to_warehouse_id} onValueChange={(v) => set("to_warehouse_id", v)}>
                  <SelectTrigger data-testid="action-transfer-warehouse">
                    <SelectValue placeholder="Select warehouse" />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses
                      .filter((w) => w.id !== item?.warehouse_id)
                      .map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            )}

            {action === "in" && (
              <>
                <Field label="Supplier">
                  <Select value={form.supplier_id} onValueChange={(v) => set("supplier_id", v)}>
                    <SelectTrigger data-testid="action-supplier-select">
                      <SelectValue placeholder="Select supplier" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.company}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Invoice / bill no.">
                  <Input data-testid="action-invoice-no-input" value={form.invoice_no}
                    onChange={(e) => set("invoice_no", e.target.value)} />
                </Field>
                <Field label="Invoice date">
                  <Input type="date" data-testid="action-invoice-date-input" value={form.invoice_date}
                    onChange={(e) => set("invoice_date", e.target.value)} />
                </Field>
                <Field label="Invoice amount ₹">
                  <Input type="number" data-testid="action-amount-input" value={form.amount}
                    onChange={(e) => set("amount", e.target.value)} />
                </Field>
                <Field label="Rate ₹ (optional)">
                  <Input type="number" data-testid="action-rate-input" value={form.rate}
                    onChange={(e) => set("rate", e.target.value)} />
                </Field>
              </>
            )}
          </div>

          <Field label={action === "in" ? "Remarks" : "Reason / remarks (required)"}>
            <Input data-testid="action-remarks-input" value={form.remarks}
              onChange={(e) => set("remarks", e.target.value)} />
          </Field>

          {action === "in" && (
            <div className="rounded border border-dashed border-border p-3">
              <div className="overline mb-1 flex items-center gap-1.5">
                <Paperclip className="h-3.5 w-3.5" /> Invoice / bill upload
              </div>
              <input type="file" multiple accept={ACCEPT} data-testid="action-bill-file-input"
                onChange={(e) => e.target.files?.length && attach(e.target.files)}
                className="w-full text-xs" />
              <label className="mt-2 inline-flex cursor-pointer items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs hover:bg-accent">
                <input type="file" accept="image/*" capture="environment" multiple hidden
                  data-testid="action-bill-camera-input"
                  onChange={(e) => e.target.files?.length && attach(e.target.files)} />
                Snap the bill with camera
              </label>
              <p className="mt-1 text-[11px] text-muted-foreground">
                PDF, JPG, PNG, DOC or DOCX. Filed under Inventory Bills / Year / Month / Supplier.
              </p>
              {uploading && (
                <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" /> Uploading…
                </div>
              )}
              {files.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1" data-testid="attached-bill-files">
                  {files.map((f) => (
                    <Badge key={f.file_id} variant="secondary" className="gap-1 text-[10px]">
                      <Paperclip className="h-3 w-3" /> {f.filename}
                      <button type="button" onClick={() => setFiles((x) => x.filter((y) => y.file_id !== f.file_id))}>
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button data-testid="stock-action-submit" onClick={submit} disabled={busy || uploading}>
            {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            {meta.cta}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
